/**
 * @weretrade/sentry - Configuration
 *
 * Centralized Sentry configuration with environment-aware DSN resolution.
 * Uses EU datacenter (de.sentry.io) for GDPR compliance.
 *
 * Organization: weretradeit
 * Projects:
 *   - weretrade-workers: Cloudflare Workers for weretrade.com
 *   - weretrade-react: Internal React apps
 *   - weretrade-public: External/public React apps
 *   - lair404-services: lair404.xyz infrastructure
 */

import type { Environment, SentryConfig } from './types';

/**
 * Sentry DSNs by project
 * These are the official DSNs for the weretradeit organization
 */
export const SENTRY_DSNS = {
  // Cloudflare Workers (weretrade.com domain)
  workers: 'https://f92e63e554586dbdd5260514f07ffe14@o4510795408932864.ingest.de.sentry.io/4510796107874384',

  // Internal React apps (protected by frontdoor-auth)
  reactInternal: 'https://17d53216fdcbd0729f1e2ec3e38342d4@o4510795408932864.ingest.de.sentry.io/4510796114034768',

  // External/public React apps
  reactPublic: 'https://d22470229202d5df0b47a787d65a42ca@o4510795408932864.ingest.de.sentry.io/4510796121964624',

  // lair404.xyz services and infrastructure
  lair404: 'https://b09ed80620fd3eb6bbba32b572c5bed1@o4510795408932864.ingest.de.sentry.io/4510796306514000',
} as const;

/**
 * Detects current environment from worker env or browser hostname.
 *
 * Detection logic:
 * - Workers: from env.ENVIRONMENT variable
 * - React: from hostname pattern (staging.*, dev.*, localhost)
 *
 * @param env - Worker environment bindings (optional)
 * @returns Detected environment
 */
export function detectEnvironment(env?: Record<string, unknown>): Environment {
  // Worker environment - check ENVIRONMENT binding
  if (env?.ENVIRONMENT) {
    const envVal = String(env.ENVIRONMENT).toLowerCase();
    if (envVal === 'staging' || envVal === 'stage') return 'staging';
    if (envVal === 'development' || envVal === 'dev') return 'development';
    return 'production';
  }

  // Browser environment - check hostname
  if (typeof (globalThis as any).window !== 'undefined') {
    const host = (globalThis as any).window.location.hostname;
    if (host.startsWith('staging.')) return 'staging';
    if (host.startsWith('dev.') || host === 'localhost') return 'development';
    return 'production';
  }

  // Default to production (safest for SSR/unknown contexts)
  return 'production';
}

/**
 * Gets Sentry DSN for the current context.
 *
 * DSN resolution:
 * 1. Direct override via env.SENTRY_DSN
 * 2. Type-specific DSN (workers vs react)
 * 3. Environment mapping (development -> staging DSN)
 *
 * @param type - Context type ('workers' or 'react')
 * @param env - Worker environment bindings (optional)
 * @returns DSN string or empty string if not configured
 */
export function getSentryDSN(
  type: 'workers' | 'react',
  env?: Record<string, unknown>
): string {
  // Allow direct DSN override
  if (env?.SENTRY_DSN) {
    return String(env.SENTRY_DSN);
  }

  // DSN resolution based on type
  // Uses defined constants with environment variable fallbacks
  if (type === 'workers') {
    // Workers use the workers DSN (same for all environments)
    return String(env?.SENTRY_DSN_WORKERS_PROD || SENTRY_DSNS.workers);
  }

  // React apps - check Vite env vars and window globals
  if (typeof (globalThis as any).window !== 'undefined') {
    // Check Vite environment variable (available at build time)
     
    // @ts-ignore
    const viteEnv = typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SENTRY_DSN;
    if (viteEnv) return String(viteEnv);

    // Fallback to window global (can be set dynamically)
    const windowEnv = ((globalThis as any).window as unknown as { __SENTRY_DSN__?: string }).__SENTRY_DSN__;
    if (windowEnv) return windowEnv;

    // Use appropriate DSN based on hostname pattern
    const host = (globalThis as any).window.location.hostname;
    if (host.includes('lair404')) return SENTRY_DSNS.lair404;
    if (host.includes('internal') || host.endsWith('.weretrade.com')) return SENTRY_DSNS.reactInternal;
    return SENTRY_DSNS.reactPublic;
  }

  return SENTRY_DSNS.workers;
}

/**
 * Gets full Sentry configuration with sensible defaults.
 *
 * @param type - Context type ('workers' or 'react')
 * @param env - Worker environment bindings (optional)
 * @param overrides - Optional config overrides
 * @returns Complete Sentry configuration
 */
export function getSentryConfig(
  type: 'workers' | 'react',
  env?: Record<string, unknown>,
  overrides?: Partial<SentryConfig>
): SentryConfig {
  const environment = detectEnvironment(env);
  const dsn = getSentryDSN(type, env);

  return {
    dsn,
    environment,
    // Lower sample rate in production to save quota
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    // Enable debug logging in non-production
    debug: environment !== 'production',
    ...overrides,
  };
}

/**
 * Gets Sentry API base URL.
 * Note: Using EU region (de.sentry.io) for GDPR compliance.
 */
export function getSentryApiUrl(): string {
  return 'https://de.sentry.io/api/0';
}

/**
 * Gets organization slug from environment.
 */
export function getSentryOrg(env?: Record<string, unknown>): string {
  return String(env?.SENTRY_ORG || 'weretradeit');
}
